"use client";

import { useEffect, useState } from "react";
import { ShoppingBag, X } from "lucide-react";
import Link from "next/link";

const TRIGGER_THRESHOLD = 3;
const COUNT_KEY = "eurolabs.cart.add_count";
const SHOWN_KEY = "eurolabs.cart.prompt_shown";

export const CART_ADD_EVENT = "eurolabs:cart-added";

/**
 * Pops up when user has added 3 items to cart — asks "Checkout or continue?"
 * Increments on each `CART_ADD_EVENT` window event; shows once per session
 * (cleared when user dismisses or navigates to checkout).
 */
export function CheckoutPromptModal() {
	const [open, setOpen] = useState(false);

	useEffect(() => {
		function onAdd() {
			if (typeof window === "undefined") return;
			if (window.sessionStorage.getItem(SHOWN_KEY)) return;
			const current = parseInt(window.localStorage.getItem(COUNT_KEY) ?? "0", 10) + 1;
			window.localStorage.setItem(COUNT_KEY, String(current));
			if (current >= TRIGGER_THRESHOLD) {
				setOpen(true);
				window.sessionStorage.setItem(SHOWN_KEY, "1");
				window.localStorage.setItem(COUNT_KEY, "0");
			}
		}
		window.addEventListener(CART_ADD_EVENT, onAdd);
		return () => window.removeEventListener(CART_ADD_EVENT, onAdd);
	}, []);

	useEffect(() => {
		if (!open) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [open]);

	if (!open) return null;

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="checkout-prompt-title"
			className="fixed inset-0 z-[100] flex items-center justify-center p-4 duration-200 animate-in fade-in"
		>
			<button
				type="button"
				aria-label="Schließen"
				onClick={() => setOpen(false)}
				className="absolute inset-0 bg-black/55 backdrop-blur-sm"
			/>

			<div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-border bg-background shadow-[0_40px_80px_-30px_rgba(8,39,74,0.45)] duration-200 animate-in zoom-in-95">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-0 opacity-[0.05]"
					style={{
						backgroundImage:
							"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
						backgroundSize: "24px 24px",
					}}
				/>

				<button
					type="button"
					onClick={() => setOpen(false)}
					aria-label="Schließen"
					className="bg-background/80 hover:border-primary/40 absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground backdrop-blur-sm transition-colors hover:text-primary"
				>
					<X className="h-3.5 w-3.5" aria-hidden />
				</button>

				<div className="relative px-6 pb-6 pt-9 sm:px-8 sm:pb-8 sm:pt-11">
					<div className="flex items-center gap-2 font-mono text-[0.58rem] uppercase tracking-[0.22em] text-primary">
						<span className="relative flex h-1.5 w-1.5">
							<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
						</span>
						3+ Produkte · Warenkorb voll
					</div>

					<h2
						id="checkout-prompt-title"
						className="mt-4 font-display text-[clamp(1.4rem,2.4vw,1.9rem)] font-medium leading-[1.1] tracking-[-0.018em] text-foreground"
					>
						Jetzt zur Kasse — oder noch weiter stöbern?
					</h2>
					<p className="mt-3 text-[0.92rem] leading-snug text-muted-foreground">
						Mehrere Peptide sind im Warenkorb. Direkt zum Checkout oder im Katalog weitersuchen?
					</p>

					<div className="mt-6 flex flex-col gap-3 sm:flex-row">
						<Link
							href="/cart"
							onClick={() => setOpen(false)}
							className="inline-flex h-14 flex-1 items-center justify-center gap-2 rounded-full bg-primary-hero font-mono text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-primary-hero-foreground shadow-md transition-colors hover:bg-primary sm:text-[0.75rem]"
						>
							<ShoppingBag className="h-4 w-4" aria-hidden />
							Zur Kasse
						</Link>
						<button
							type="button"
							onClick={() => setOpen(false)}
							className="hover:bg-primary-hero/5 inline-flex h-14 flex-1 items-center justify-center gap-2 rounded-full border border-border bg-background font-mono text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-foreground transition-colors hover:border-primary-hero sm:text-[0.75rem]"
						>
							Weiter shoppen
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
